import { z } from 'zod';

import type {
  BuildShipmentTargetRequest,
  BuildShipmentTargetResponse,
  ShipmentTargetAssessment,
  ShipmentTargetPlanningSummary,
  ShipmentTargetRange,
  ShipmentTargetScenario,
  ShipmentTargetTotals,
  ShipmentTargetWarning,
} from './build-shipment-target.dto.js';
import {
  IsoDateTimeSchema,
  OFFICIAL_PROXY_TOLERANCE_PCT,
  ShipmentTargetOfficialProxyStatusSchema,
  ShipmentTargetOperationalStatusSchema,
  ShipmentTargetScenarioLevelSchema,
  ShipmentTargetWarningCodeSchema,
} from './shipments-types.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);
const positiveNumberSchema = z.number().gt(0);
const percentageSchema = z.number().min(0).max(100);

const ShipmentTargetTotalsSchema: z.ZodType<ShipmentTargetTotals> = z
  .object({
    swCrvLb: nonNegativeNumberSchema,
    redemptionWeightLb: nonNegativeNumberSchema,
    scrapWeightLb: nonNegativeNumberSchema,
    totalWeightLb: nonNegativeNumberSchema,
    refundValueCents: nonNegativeIntSchema,
  })
  .strict();

const ShipmentTargetRangeSchema: z.ZodType<ShipmentTargetRange> = z
  .object({
    rangeStartAt: IsoDateTimeSchema,
    rangeEndAt: IsoDateTimeSchema,
  })
  .strict();

const ShipmentTargetPlanningSummarySchema: z.ZodType<ShipmentTargetPlanningSummary> = z
  .object({
    targetTotalWeightLb: positiveNumberSchema,
    operationalLossPct: percentageSchema,
    operationalCeilingLb: positiveNumberSchema,
    officialProxyTolerancePct: z.literal(OFFICIAL_PROXY_TOLERANCE_PCT),
    officialProxyCeilingLb: positiveNumberSchema,
  })
  .strict();

const ShipmentTargetAssessmentSchema: z.ZodType<ShipmentTargetAssessment> = z
  .object({
    operationalLossStatus: ShipmentTargetOperationalStatusSchema,
    officialProxyStatus: ShipmentTargetOfficialProxyStatusSchema,
    level: ShipmentTargetScenarioLevelSchema,
    minimumOfficialReceivedFor2_5ProxyLb: nonNegativeNumberSchema,
  })
  .strict();

const ShipmentTargetScenarioSchema: z.ZodType<ShipmentTargetScenario> = z
  .object({
    lineItemIds: z.array(z.uuid()),
    purchaseCommittedIds: z.array(z.uuid()),
    totals: ShipmentTargetTotalsSchema,
    computedRange: ShipmentTargetRangeSchema,
    assessment: ShipmentTargetAssessmentSchema,
  })
  .strict();

const ShipmentTargetWarningSchema: z.ZodType<ShipmentTargetWarning> = z
  .object({
    code: ShipmentTargetWarningCodeSchema,
    message: z.string().trim().min(1),
  })
  .strict();

export const BuildShipmentTargetRequestSchema: z.ZodType<BuildShipmentTargetRequest> = z
  .object({
    companyId: z.uuid(),
    rcScopeId: z.uuid(),
    materialId: z.uuid(),
    targetLb: positiveNumberSchema,
    operationalLossPct: percentageSchema.optional().default(0),
  })
  .strict();

export const BuildShipmentTargetResponseSchema: z.ZodType<BuildShipmentTargetResponse> = z
  .object({
    planningSummary: ShipmentTargetPlanningSummarySchema,
    scenarioA: ShipmentTargetScenarioSchema,
    scenarioB: ShipmentTargetScenarioSchema,
    warnings: z.array(ShipmentTargetWarningSchema),
  })
  .strict();
